import {
  WSElement,
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll
} from "@wingspanhq/fe-component-library";
import { CustomFieldResourceType } from "@wingspanhq/payments/dist/interfaces";
import { SearchPayeeRowsFilterParameter } from "@wingspanhq/wingspan-api-sdk";
import { queriesEngagements, queriesPayeeRows } from "@query";
import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useQueryCustomFieldsAll } from "../../../../query/customFields/queries/useQueryCustomFieldsAll";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryRequirementDefinitionListAll } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinitionListAll";
import { useQueryAllOrganizationUsers } from "../../../../query/users/queries";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useUrlQueryFilters } from "../../../../shared/hooks/useUrlQueryFilters";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { useGoToContractorImportStart } from "../../../BulkImporter/paths";
import { useBulkCollaboratorBatchesQuery } from "../../../BulkImporter/query/bulkCollaborator/queries";
import { useCustomization } from "../../../customization";
import { useCreateContractorModal } from "../../components/CreateContractorModal/useCreateContractorModal";
import { TablePayees } from "../../components/TablePayees/TablePayees";
import {
  PAYEES_ARCHIVED_PATH,
  PAYEES_COMPLETE_PATH,
  PAYEES_INCOMPLETE_PATH,
  PAYEES_NOT_SIGNED_UP_PATH
} from "../../utils";
import {
  ContractorsTabsFilters,
  ContractorTab
} from "../RouteDashboard/DashboardTabs";
import { AlertBulkReview } from "./AlertBulkReview";
import { buildConfig, getInterceptFiltersChange } from "./filters/config";
import { createListRequest } from "./filters/request";
import { PayeeFilterEligibilityStatus, PayeeFilters } from "./filters/types";

/**
 * The dashboard tab paths that carry a fixed v3 filter. The "Active" tab
 * (`PAYEES_ALL_PATH`) is absent — it uses the sidebar filters instead, matching
 * the v1 behavior.
 */
const TAB_BY_PATH: Record<string, ContractorTab> = {
  [PAYEES_INCOMPLETE_PATH]: "incomplete",
  [PAYEES_COMPLETE_PATH]: "complete",
  [PAYEES_ARCHIVED_PATH]: "archived",
  [PAYEES_NOT_SIGNED_UP_PATH]: "notSignedUp"
};

export const RoutePayees: React.FC<RouteComponentProps> = props => {
  const { terminology } = useCustomization();
  useBrowserPageTitle(terminology().sendPaymentsContractor);

  const { filters, setFilters } = useUrlQueryFilters<PayeeFilters>();
  const xWingspanAccount = useXWingspanExpansion();
  const modalCreateContractor = useCreateContractorModal();
  const goToContractorImportStart = useGoToContractorImportStart();

  const queryFeatureFlags = useFeatureFlags();
  const queryCustomFields = useQueryCustomFieldsAll({
    resourceType: [CustomFieldResourceType.Collaborator]
  });
  const queryOrgAccounts = useQueryAllOrganizationUsers();
  const queryEngagementList = queriesEngagements.listEngagements.useQueryAll(
    undefined,
    { refetchOnMount: false, retry: false }
  );
  const queryBulkPayableBatches = useBulkCollaboratorBatchesQuery();
  const queryRequirements = useQueryRequirementDefinitionListAll();

  const tab = TAB_BY_PATH[props.match.path];
  const hasTabFilters = !!tab;

  const filter: SearchPayeeRowsFilterParameter = useMemo(
    () =>
      tab
        ? ContractorsTabsFilters[tab]
        : createListRequest({ ...filters }, queryCustomFields.data).filter,
    [filters, queryCustomFields.data, tab]
  );

  const queryPayeeRows = queriesPayeeRows.searchPayeeRows.usePaginatedQuery({
    filter,
    xWingspanAccount
  });
  const payees = queryPayeeRows.flatData ?? [];
  const totalCount = queryPayeeRows.data?.pages?.[0]?.pagination?.totalSize;

  // Gate the whole filter bar on there being any contractor at all (any tab),
  // mirroring the v1 "all payees" summary gate.
  const queryAnyPayees = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: ContractorsTabsFilters.all,
      page: { size: 1 },
      xWingspanAccount
    },
    { select: list => list.pagination?.totalSize }
  );

  const primaryFiltersConfig = useMemo(
    () =>
      buildConfig({
        orgAccounts: queryOrgAccounts.data || [],
        engagements: (queryEngagementList.flatData || []).filter(
          e => !e.isDefault
        ),
        bulkBatches: queryBulkPayableBatches.data || [],
        customFields: queryCustomFields.data || [],
        requirements: queryRequirements.data || []
      }),
    [
      queryBulkPayableBatches.data,
      queryCustomFields.data,
      queryEngagementList.flatData,
      queryOrgAccounts.data,
      queryRequirements.data
    ]
  );

  const interceptFiltersChange = useMemo(
    () => getInterceptFiltersChange(queryRequirements.data || []),
    [queryRequirements.data]
  );

  const hasActiveFilters = Object.keys(filters).length > 0;

  const renderBody = () => {
    if (queryPayeeRows.isLoading) {
      return (
        <WSElement mt="M">
          <TablePayees payees={[]} loading isArchivedOnly={hasTabFilters} />
        </WSElement>
      );
    }

    if (queryPayeeRows.isError) {
      return (
        <WSEmptyState
          py="3XL"
          mt="L"
          hasBorder
          bgColor="gray"
          type="error"
          title="Couldn’t load contractors"
          description="Something went wrong while loading contractors."
          actions={{
            buttons: [
              {
                label: "Try again",
                kind: "Secondary",
                onClick: () => queryPayeeRows.refetch()
              }
            ]
          }}
        />
      );
    }

    if (payees.length === 0) {
      return hasActiveFilters ? (
        <WSEmptyState
          py="3XL"
          mt="L"
          hasBorder
          bgColor="gray"
          type="search"
          title="No results found"
          description="Try adjusting your search or filter to find what you’re looking for."
        />
      ) : (
        <WSEmptyState
          py="3XL"
          mt="L"
          hasBorder
          type="people"
          bgColor="gray"
          title="No contractors yet"
          description={
            hasTabFilters
              ? undefined
              : "Once a contractor is added, they will appear here."
          }
          actions={
            hasTabFilters
              ? undefined
              : {
                  buttons: [
                    {
                      label: "Add contractor",
                      kind: "Secondary",
                      onClick: () => {
                        modalCreateContractor.open({ redirectToDetails: true });
                      }
                    },
                    {
                      label: "Bulk upload contractors",
                      kind: "Tertiary",
                      onClick: async () => {
                        goToContractorImportStart();
                      }
                    }
                  ]
                }
          }
        />
      );
    }

    return (
      <>
        {queryFeatureFlags.data?.requirementsBulkReview &&
          (filters.eligibilityStatus ===
            PayeeFilterEligibilityStatus.Incomplete ||
            filters.eligibilityStatus ===
              PayeeFilterEligibilityStatus.SpecificRequirement) && (
            <AlertBulkReview payeeFilters={filters} />
          )}
        <WSInfiniteScroll
          mt="M"
          onLoad={() => queryPayeeRows.fetchNextPage()}
          loadMore={payees.length > 0}
          endOfList={!queryPayeeRows.hasNextPage}
          loading={!!queryPayeeRows.isFetchingNextPage}
        >
          <TablePayees payees={payees} isArchivedOnly={hasTabFilters} />
        </WSInfiniteScroll>
      </>
    );
  };

  return (
    <>
      {!!queryAnyPayees.data && (
        <WSFilters<PayeeFilters>
          my="L"
          searchable
          values={filters}
          onFilter={setFilters}
          interceptChange={interceptFiltersChange}
          primaryFilters={primaryFiltersConfig}
          hidePrimaryFiltersButton={hasTabFilters}
          info={totalCount === undefined ? undefined : { count: totalCount }}
        />
      )}
      {renderBody()}
    </>
  );
};
